import { useEffect, useMemo, useCallback } from 'react';
import { Box, Button, CircularProgress, Grid } from '@mui/material';
import { LoaderWrapper } from '@styles/StyledDiv';

import { CheckboxField } from '@shared/fields/CheckboxField';
import { SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { SectionTitle } from '@shared/components/SectionTitle';
import { ConsentPreferences, ConsentPurposes } from '@modules/ContactManager/components/constants';
import { IOptInFormProps } from './interfaces';
import { IOneTrustFormFieldConfig, OptOutOneTrustForm } from './OptOutOneTrustForm';

export const OptOutForm = ({
  items,
  isLoading,
  isActiveDeal,
  unsubscribeChecked,
  setUnsubscribeChecked,
  handleUndoChanges,
  handleUnsubscribeChange,
  values,
  setFieldValue,
  dirty,
}: IOptInFormProps) => {
  // Handle parent checkbox change
  const handleParentCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parentName = e?.target?.name;
      const isChecked = !values?.[parentName];
      if (isChecked) {
        setUnsubscribeChecked(({ checkRender }) => ({ isChecked: false, checkRender: checkRender }));
      }
      setFieldValue?.(`${parentName}`, isChecked);
      if (parentName !== ConsentPurposes.MARKETING) {
        setFieldValue?.(`${parentName}_Call`, isChecked);
      }
      setFieldValue?.(`${parentName}_Email`, isChecked);
      setFieldValue?.(`${parentName}_SMS`, isChecked);
    },
    [setFieldValue, values]
  );
  const formConfig: IOneTrustFormFieldConfig<{
    width: string;
    name: string;
    label: string;
    component: JSX.Element;
  }>[] = useMemo(
    () => [
      {
        width: '100%',
        name: ConsentPurposes.APPLICATION,
        label: 'Active or Inactive Loan Application (or Lead) related emails (Non Promotional)',
        component: CheckboxField,
        field: { onChange: handleParentCheckbox, name: ConsentPurposes.APPLICATION, value: values?.Application },
        disabled: isActiveDeal,
      },
      {
        width: '10%',
        name: ConsentPreferences.APPLICATION_SMS,
        label: 'SMS',
        component: CheckboxField,
        isSubForm: true,
      },
      {
        width: '10%',
        name: ConsentPreferences.APPLICATION_EMAIL,
        label: 'Email',
        component: CheckboxField,
        isSubForm: true,
        disabled: isActiveDeal,
      },
      {
        width: '10%',
        name: ConsentPreferences.APPLICATION_CALL,
        label: 'Call',
        component: CheckboxField,
        isSubForm: true,
        disabled: isActiveDeal,
      },
      {
        width: '90%',
        name: ConsentPurposes.ADDITIONAL,
        label: 'Additional products GAP,etc.',
        component: CheckboxField,
        field: { onChange: handleParentCheckbox, name: 'Additional', value: values?.Additional },
      },
      {
        width: '10%',
        name: ConsentPreferences.ADDITIONAL_SMS,
        label: 'SMS',
        component: CheckboxField,
        isSubForm: true,
      },
      {
        width: '10%',
        name: ConsentPreferences.ADDITIONAL_EMAIL,
        label: 'Email',
        component: CheckboxField,
        isSubForm: true,
      },

      {
        width: '10%',
        name: ConsentPreferences.ADDITIONAL_CALL,
        label: 'Call',
        component: CheckboxField,
        isSubForm: true,
      },
      {
        width: '90%',
        name: ConsentPurposes.MARKETING,
        label: 'Marketing Offers and Updates or Referral Notifications',
        component: CheckboxField,
        field: { onChange: handleParentCheckbox, name: 'Marketing', value: values?.Marketing },
      },
      {
        width: '10%',
        name: ConsentPreferences.MARKETING_SMS,
        label: 'SMS',
        component: CheckboxField,
        isSubForm: true,
      },
      {
        width: '10%',
        name: ConsentPreferences.MARKETING_EMAIL,
        label: 'Email',
        component: CheckboxField,
        isSubForm: true,
      },
      {
        width: '90%',
        name: ConsentPurposes.RAMP,
        label: 'R.A.M.P (Refinanced Auto Monitoring Program)',
        component: CheckboxField,
        field: { onChange: handleParentCheckbox, name: 'RAMP', value: values?.RAMP },
        disabled: !values?.RAMP 
      },
      {
        width: '90%',
        name: 'Unsubscribe',
        label: 'Unsubscribe All',
        component: CheckboxField,
        field: { onChange: handleUnsubscribeChange, name: 'Unsubscribe', value: unsubscribeChecked?.isChecked },
      },
    ],
    [
      isActiveDeal,
      handleParentCheckbox,
      values?.Application,
      values?.Additional,
      values?.Marketing,
      values?.RAMP,
      handleUnsubscribeChange,
      unsubscribeChecked,
    ]
  );

  useEffect(() => {
    const isApplicationParentChecked = values?.Application_Call || values?.Application_Email || values?.Application_SMS;
    if (isApplicationParentChecked !== values?.Application) {
      setFieldValue?.(ConsentPurposes.APPLICATION, isApplicationParentChecked);
      setUnsubscribeChecked(({ checkRender }) => ({ isChecked: false, checkRender: checkRender }));
    }
    //When active deal is true, to track Application_SMS check box changes requires separate condition to set unsubscribe checkbox to false.
    if (values?.Application_SMS) {
      setUnsubscribeChecked(({ checkRender }) => ({ isChecked: false, checkRender: checkRender }));
    }
    const isAdditionalParentChecked = values?.Additional_Call || values?.Additional_Email || values?.Additional_SMS;
    if (isAdditionalParentChecked !== values?.Additional) {
      setFieldValue?.(ConsentPurposes.ADDITIONAL, isAdditionalParentChecked);
      setUnsubscribeChecked(({ checkRender }) => ({ isChecked: false, checkRender: checkRender }));
    }

    const isMarketingParentChecked = values?.Marketing_Email || values?.Marketing_SMS;
    if (isMarketingParentChecked !== values?.Marketing) {
      setFieldValue?.(ConsentPurposes.MARKETING, isMarketingParentChecked);
      setUnsubscribeChecked(({ checkRender }) => ({ isChecked: false, checkRender: checkRender }));
    }
  }, [values, setFieldValue, dirty]);

  return (
    <>
      <Box>
        {isLoading ? (
          <Grid container>
            <LoaderWrapper>
              <CircularProgress />
            </LoaderWrapper>
          </Grid>
        ) : (
          <>
            {!!items.length && (
              <>
                <SectionTitle
                  title="Communication Preferences (Uncheck for Opt-Out)"
                  color="primary"
                  marginBottom={1}
                />
              </>
            )}
            <>
              <Grid
                container
                rowSpacing={3}
                columnSpacing={3}
              >
                <OptOutOneTrustForm config={formConfig} />
              </Grid>
            </>
          </>
        )}
      </Box>
      {!isLoading && (
        <CommonButtonWrapper>
          <Button
            variant="outlined"
            sx={{ mr: 2 }}
            disabled={!items.length}
            onClick={() => handleUndoChanges()}
          >
            Undo Changes
          </Button>

          <SaveButton
            width="150px"
            variant="contained"
            label="Update Preferences"
            disabled={!items.length}
          />
        </CommonButtonWrapper>
      )}
    </>
  );
};
